import * as React from 'react';

import { FlyerData } from '../data/FlyerData';
import { getStoreState, storeDispatch } from '../state/Store';
import {
  getTemplates,
  getTemplatesCategories,
  updateTemplateVisibility
} from '../utils/FirebaseUtils';
import * as Actions from '../state/actions';
import {
  canHover,
  generateClassName,
  getFileSize,
  isMobileLayout,
  isSuperUser,
  pause
} from '../utils';
import newFileIcon from '../components/overlay-icons/new-file.svg';
import { OverlayIcon } from 'components/OverlayIcon';
import Size from 'data/Size';
import SelectRich from 'components/SelectRich';
import SelectOption from 'components/SelectOption';
import { matchResolutionWithStandards, Resolution } from 'data/Resolution';
import ResolutionOption from 'showcase/components/ResolutionOption';
import { trackEvent } from 'utils/Tracker';
import { debounce } from 'debounce';
import { TemplateCategory } from 'data/TemplateCategory';
import { useMobileLayout } from 'hooks/useMobileLayout';
import { PlatformType } from 'data/PlatformType';
import styled from 'styled-components';
import { imageIconUrl } from 'components/OverlayOptions';

const PromoBox = styled.div`
  text-align: left;
  font-size: 15px;
  line-height: 1.25;
  color: var(--secondary-text-color);
  border-top: 1px solid var(--menu-divider-color);
  padding: 16px 0;

  h2 {
    font-size: 15px;
    line-height: 1.25;
    margin-bottom: 4px;
    color: var(--primary-text-color);
  }
`;

interface FlyerGridItemProps {
  data: FlyerData;
  onSelect: (data: FlyerData) => void;
  isCategory?: boolean;
}
interface FlyerGridItemState {
  videoUrl: string | null;
  videoReady: boolean;
  thumbLoaded: boolean;
}
class FlyerGridItem extends React.Component<
  FlyerGridItemProps,
  FlyerGridItemState
> {
  state: FlyerGridItemState = {
    videoUrl: null,
    videoReady: false,
    thumbLoaded: false
  };
  isOver = false;
  debouncedMove: any;

  constructor(props: FlyerGridItemProps) {
    super(props);

    this.debouncedMove = debounce(this.didMove, 100);
  }

  componentWillUnmount(): void {
    this.debouncedMove.clear();
  }

  didMove = async () => {
    this.isOver = true;
    const { thumbnailUrl } = this.props.data;
    if (thumbnailUrl) {
      const videoUrl = thumbnailUrl.replace('-thumb.jpg', '-preview.mp4');
      // check if video exists
      try {
        const videoSize = await getFileSize(videoUrl);
        if (videoSize) {
          if (this.isOver) {
            this.setState({ videoUrl });
          }
        }
      } catch {
        // do nothing
      }
    }
  };
  didLeave = () => {
    this.debouncedMove.clear();
    this.isOver = false;
    if (this.state.videoUrl) {
      this.setState({ videoUrl: null, videoReady: false });
    }
  };
  didClick = () => {
    this.props.onSelect(this.props.data);
  };
  editTemplate = () => {
    getStoreState().app.history.push(`/template/${this.props.data.id}`);
  };

  togglePublish = async () => {
    const { data } = this.props;
    await updateTemplateVisibility(data.id!, data.published ? false : true);
    location.reload();
  };

  videoIsReady = () => {
    if (this.state.videoUrl) {
      this.setState({ videoReady: true });
    }
  };

  renderThumb = (content: JSX.Element): JSX.Element => {
    const {
      isCategory,
      data: { templateOptions }
    } = this.props;
    const isProTemplate = !isCategory && templateOptions?.isPro === true;
    if (isProTemplate) {
      return (
        <div className="flyers-grid-item-thumb-pro-wrapper">{content}</div>
      );
    } else {
      return <>{content}</>;
    }
  };

  didLoad = () => {
    this.setState({ thumbLoaded: true });
  };

  render() {
    const {
      isCategory,
      data: { thumbnailUrl, published }
    } = this.props;
    const { videoUrl, videoReady } = this.state;
    const { user } = getStoreState().app;
    const canEditTemplate = isSuperUser(user) && !isCategory;
    const hover = canHover();
    return (
      <div
        className={generateClassName('flyers-grid-item', {
          unpublished: published !== true,
          loaded: this.state.thumbLoaded
        })}
      >
        <div className="flyers-grid-item-thumb">
          {this.renderThumb(
            <>
              {isCategory && (
                <>
                  <img
                    className="back-image"
                    style={{ transform: `rotate(2deg)` }}
                    src={thumbnailUrl}
                    // crossOrigin="anonymous"
                  />
                  <img
                    className="back-image"
                    style={{ transform: 'rotate(-3deg)' }}
                    src={thumbnailUrl}
                    // crossOrigin="anonymous"
                  />
                </>
              )}
              <img
                src={thumbnailUrl}
                // crossOrigin="anonymous"
                // style={{ width, height }}
                onClick={this.didClick}
                onMouseMove={hover ? this.debouncedMove : undefined}
                onMouseOut={hover ? this.didLeave : undefined}
                draggable={false}
                onLoad={this.didLoad}
              />
              {videoUrl && (
                <video
                  muted={true}
                  autoPlay={true}
                  loop={true}
                  src={videoUrl}
                  crossOrigin="Anonymous"
                  onClick={this.didClick}
                  onCanPlay={this.videoIsReady}
                  style={{ display: videoReady ? 'block' : 'none' }}
                />
              )}
            </>
          )}
          {canEditTemplate && (
            <div className="edit-options">
              <button onClick={this.togglePublish}>
                {published ? 'Unpublish' : 'Publish'}
              </button>
              <button onClick={this.editTemplate}>Edit</button>
            </div>
          )}
        </div>
        {canEditTemplate && (
          <div className="metadata">
            {this.props.data.id + ': ' + this.props.data.categories?.join(',')}
          </div>
        )}
      </div>
    );
  }
}

type SortType = 'newest' | 'popular';

interface FlyersGridProps {
  items: FlyerData[];
}

class FlyersGrid extends React.Component<FlyersGridProps> {
  onSelect = (item: FlyerData) => {
    storeDispatch(Actions.createFlyerFromTemplate(item));
  };
  render() {
    return (
      <div className="flyers-grid">
        {this.props.items.map(item => (
          <FlyerGridItem data={item} key={item.id!} onSelect={this.onSelect} />
        ))}
      </div>
    );
  }
}
/*
interface RecentFlyersState {
  flyers: FlyerData[] | null;
  hasMore: boolean;
}
class RecentFlyers extends React.Component {
  isLoading = false;
  _mounted = false;
  state: RecentFlyersState = {
    flyers: null,
    hasMore: false
  };
  componentDidMount() {
    this._mounted = true;
    const { user } = getStoreState().app;
    if (user) {
      this.loadFlyers(user.uid);
      this.isLoading = true;
    }
  }
  componentWillUnmount() {
    this._mounted = false;
  }
  componentDidUpdate() {
    const { user } = getStoreState().app;
    if (user && !this.isLoading) {
      this.loadFlyers(user.uid);
      this.isLoading = true;
    }
  }
  async loadFlyers(uid: string) {
    let flyers = await getFlyers(uid);
    if (this._mounted) {
      let hasMore = false;
      if (flyers.length > 5) {
        // truncate
        flyers = flyers.slice(0, 5);
        hasMore = true;
      }
      this.setState({ flyers, hasMore });
      this.isLoading = false;
    }
  }
  showAllFlyers = () => {
    storeDispatch(Actions.showLibrary());
  };
  renderGrid(): JSX.Element {
    return (
      <>
        <FlyersGrid items={this.state.flyers!} />
        {this.state.hasMore && (
          <button className="tertiary large" onClick={this.showAllFlyers}>
            View all Your flyers
          </button>
        )}
      </>
    );
  }
  render() {
    const { user } = getStoreState().app;
    if (!user) {
      return null;
    }
    return (
      <div>
        <h2>Open recent</h2>
        {this.state.flyers ? (
          this.state.flyers.length ? (
            this.renderGrid()
          ) : (
            <div>No flyers saved.</div>
          )
        ) : (
          <div>Loading...</div>
        )}
      </div>
    );
  }
}
*/

interface TemplatesGridState {
  templates: FlyerData[];
  categories: string[];
  sizes: Resolution[];
  selectedSize: Size;
  sorting: SortType;
}

const ALL_CATEGORY = 'all';
const ANY_SIZE: Size = {
  width: 0,
  height: 0
};

let cachedCategories: string[];
let cachedSizes: Resolution[];

let templatesCategories: TemplateCategory[] = [];

interface TemplatesGridProps {
  selectedCategory: string | undefined;
  isMobile: boolean;
}
class TemplatesGrid extends React.Component<
  TemplatesGridProps,
  TemplatesGridState
> {
  _mounted = true;
  state: TemplatesGridState = {
    templates: [],
    categories: [],
    sizes: [],
    selectedSize: ANY_SIZE,
    sorting: 'newest'
  };

  el = React.createRef<HTMLDivElement>();

  async componentDidMount() {
    const { allTemplates } = getStoreState().app;
    if (!templatesCategories.length) {
      templatesCategories = await getTemplatesCategories();
    }
    if (allTemplates) {
      this.setState({
        templates: allTemplates,
        categories: cachedCategories,
        sizes: cachedSizes
      });
      return;
    }
    // load templates
    let templates = await getTemplates();
    // if (isRunningInProduction()) {
    storeDispatch({ type: Actions.DID_LOAD_TEMPLATES, templates });
    // }
    let categories: string[] = [];
    const sizes: Resolution[] = [];
    templates.forEach(t => {
      if (t.categories) {
        categories = categories.concat(t.categories);
      }
      let hasResolution = false;
      for (const s of sizes) {
        if (
          s.width === t.resolution.width &&
          s.height === t.resolution.height
        ) {
          hasResolution = true;
          break;
        }
      }
      if (!hasResolution) {
        sizes.push(t.resolution);
      }
    });
    categories = categories.filter(
      (value: string, index: number, self: string[]) => {
        return self.indexOf(value) === index;
      }
    );
    categories.sort();
    // replace sizes with standard resolutions
    for (let i = 0; i < sizes.length; i++) {
      const size = sizes[i];
      const matchedResolution = matchResolutionWithStandards(size);
      if (matchedResolution) {
        sizes[i] = matchedResolution;
      }
    }
    cachedCategories = categories;
    cachedSizes = sizes;
    if (this._mounted) {
      this.setState({ templates, categories, sizes });
    }
  }
  componentWillUnmount() {
    this._mounted = false;
  }

  scrollToTop() {
    this.el.current?.parentElement?.scrollTo?.(0, 0);
  }

  didSelectCategory = (selectedCategory: string) => {
    if (selectedCategory !== ALL_CATEGORY) {
      trackEvent('filterTemplatesCategory', { category: selectedCategory });
    }
    getStoreState().app.history.push(`/templates/${selectedCategory}`);
    this.scrollToTop();
  };

  didSelectSize = (selectedSize: Resolution) => {
    if (selectedSize !== ANY_SIZE) {
      trackEvent('filterTemplatesSize', {
        size: `${selectedSize.width}x${selectedSize.height}`
      });
    }
    this.setState({ selectedSize });
    this.scrollToTop();
  };

  didSelectSorting = (sorting: SortType) => {
    this.setState({ sorting });
    this.scrollToTop();
  };

  onSelectCategory = (item: FlyerData) => {
    const category = templatesCategories.find(
      cat => cat.coverTemplateId === item.id
    );
    if (category) {
      trackEvent('filterTemplatesCategory', { category: category.id });
      getStoreState().app.history.push(`/templates/${category.id}`);
      this.scrollToTop();
    }
  };

  renderCategoriesSelection(): JSX.Element {
    return (
      <>
        <div className="flyers-grid">
          {templatesCategories.map((cat, index) => {
            const data = this.state.templates.find(
              template => template.id === cat.coverTemplateId
            );
            if (!data) {
              console.log(
                'Could not find data for template: ' + cat.coverTemplateId
              );
              return null;
            }
            return (
              <div key={index}>
                <FlyerGridItem
                  data={data}
                  onSelect={this.onSelectCategory}
                  isCategory={true}
                />
                <h3>{cat.name}</h3>
              </div>
            );
          })}
        </div>
        {this.props.isMobile && (
          <PromoBox>
            <h2>Do more on your computer</h2>
            <div>
              Create from scratch, or import your designs, visiting{' '}
              <a
                href={`https://beatflyer.com${
                  getStoreState().app.platform === PlatformType.iOSApp
                    ? '/?ref=ios'
                    : ''
                }`}
                target="_blank"
                rel="noopener noreferrer"
              >
                beatflyer.com
              </a>{' '}
              with your desktop computer. Tweak and export your creations here{' '}
              <strong>with a single subscription</strong> 🚀
            </div>
          </PromoBox>
        )}
      </>
    );
  }

  render() {
    const { selectedCategory, isMobile } = this.props;
    const { categories, sizes, selectedSize, sorting } = this.state;
    const { user } = getStoreState().app;
    const canSeeUnpublishedTemplates = isSuperUser(user);
    const templates = this.state.templates
      .filter(t => {
        return canSeeUnpublishedTemplates || t.published === true;
      })
      .filter(t => {
        if (t.uid) {
          return (
            user !== null && (user.uid === t.uid || canSeeUnpublishedTemplates)
          );
        }
        return true;
      })
      .filter(t => {
        if (selectedCategory === undefined) {
          return true;
        }
        return (
          selectedCategory === ALL_CATEGORY ||
          (t.categories && t.categories.includes(selectedCategory))
        );
      })
      .filter(t => {
        return (
          selectedSize === ANY_SIZE ||
          (t.resolution.width === selectedSize.width &&
            t.resolution.height === selectedSize.height)
        );
      })
      .sort((a, b) => {
        if (sorting === 'newest') {
          return (b.date ?? 0) - (a.date ?? 0);
        } else {
          return (b.usageCount ?? 0) - (a.usageCount ?? 0);
        }
      })
      .sort((a, b) => {
        if (user) {
          if (a.uid && a.uid === user.uid && b.uid && b.uid === user.uid) {
            return 0;
          }
          if (a.uid && a.uid === user.uid) {
            return -1;
          }
          if (b.uid && b.uid === user.uid) {
            return 1;
          }
        }
        return 0;
      });

    const centerStyle: React.CSSProperties = {
      height: 'calc(100% - 80px)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center'
    };

    return (
      <div
        style={
          isMobile && this.state.templates.length === 0
            ? centerStyle
            : undefined
        }
        ref={this.el}
      >
        {!isMobile && (
          <h2 style={{ marginBottom: 40 }}>Or start from a template</h2>
        )}

        {this.state.templates.length ? (
          selectedCategory === undefined ? (
            this.renderCategoriesSelection()
          ) : (
            <>
              <div className="templates-filters">
                <SelectRich
                  legend="Category"
                  options={[ALL_CATEGORY].concat(categories)}
                  value={selectedCategory}
                  itemRenderer={SelectOption}
                  onSelect={this.didSelectCategory}
                />
                <SelectRich
                  legend="Size"
                  options={[ANY_SIZE].concat(sizes)}
                  value={selectedSize}
                  formatter={(value: Resolution) => {
                    if (value === ANY_SIZE) {
                      return 'any';
                    }
                    return value.width + ' x ' + value.height;
                  }}
                  itemRenderer={ResolutionOption}
                  onSelect={this.didSelectSize}
                />
                {!isMobileLayout() && (
                  <SelectRich
                    legend="Sort"
                    options={['newest', 'popular']}
                    value={sorting}
                    itemRenderer={SelectOption}
                    onSelect={this.didSelectSorting}
                  />
                )}
              </div>
              {templates.length === 0 &&
              (selectedCategory !== ALL_CATEGORY ||
                selectedSize !== ANY_SIZE) ? (
                <div>No templates with current filters</div>
              ) : (
                <FlyersGrid items={templates} />
              )}
            </>
          )
        ) : (
          <div
            style={{
              color: 'var(--secondary-text-color)',
              lineHeight: 'var(--grid-thumb-size)'
            }}
          >
            Loading categories
            <span className="ellipsis-anim">
              <span>.</span>
              <span>.</span>
              <span>.</span>
            </span>
          </div>
        )}
      </div>
    );
  }
}

interface NewFlyerSelectorProps {
  match: any;
}
export const NewFlyerSelector: React.FC<NewFlyerSelectorProps> = ({
  match
}) => {
  const createEmpty = React.useCallback(() => {
    storeDispatch(Actions.createEmptyFlyer());
  }, []);

  const importDesign = React.useCallback(async () => {
    storeDispatch(
      Actions.changeResolution({ width: 1080, height: 1080, name: 'import' })
    );
    await pause(1);
    storeDispatch(Actions.importDesign());
  }, []);

  const isMobile =
    useMobileLayout() || getStoreState().app.platform === PlatformType.iOSApp;

  return (
    <div className="new-flyer">
      {!isMobile && (
        <div
          style={{
            display: 'flex',
            gap: 'var(--grid-thumb-gap)',
            justifyContent: 'center'
          }}
        >
          <div className="new-flyer-fresh">
            {/* <h2>Start fresh</h2> */}
            <div className="new-flyer-fresh-thumb" onClick={createEmpty}>
              <OverlayIcon imageUrl={newFileIcon} />
              <button className="primary large" onClick={createEmpty}>
                Create empty
              </button>
            </div>
          </div>
          <div className="new-flyer-fresh">
            {/* <h2>Start fresh</h2> */}
            <div className="new-flyer-fresh-thumb" onClick={importDesign}>
              <OverlayIcon imageUrl={imageIconUrl} />
              <button className="primary large" onClick={importDesign}>
                Import design
              </button>
            </div>
          </div>
        </div>
      )}

      {/* <RecentFlyers /> */}
      <TemplatesGrid
        selectedCategory={match.params.category}
        isMobile={isMobile}
      />
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/library/NewFlyerSelector.tsx